// Packages
import React, { useState, useEffect } from "react";

// MUI
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  List,
  ListItem,
  Typography,
  Stack,
  CircularProgress,
  ListItemButton,
} from "@mui/material";

import { DEVICE_STATUS_TAG, STATUS_TAG_LABEL } from "const";

// Components
import CollapseCard from "components/Collapse";
import StatusTag from "components/StatusTag";
import PaginationField from "components/Pagination";

const TitleTypography = styled(Typography)(({ theme }) => ({
  margin: 0,
  color: theme.palette.primary.main,
  fontWeight: theme.typography.customFontWeight[2],
  fontSize: theme.typography.customFontSize[3],
  textAlign: "left",
  minWidth: 160,
}));

const ContentTypography = styled(Typography)(({ theme }) => ({
  margin: 0,
  color: theme.palette.primary.black,
  fontSize: theme.typography.customFontSize[2],
  minWidth: 160,
}));

const ManagedContent = ({ pushPage, managedDevicesData, auth }) => {
  const theme = useTheme();

  const [managedData, setManagedData] = useState([]);
  const [managedLoading, setManagedLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const totalCount = managedData?.length ?? 0;

  const startRecord = totalCount === 0 ? 0 : currentPage * rowsPerPage + 1;

  const endRecord =
    totalCount === 0
      ? 0
      : Math.min((currentPage + 1) * rowsPerPage, totalCount);

  useEffect(() => {
    setManagedLoading(true);
    setManagedData(managedDevicesData || []);
    setManagedLoading(false);
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 2,
        boxSizing: "border-box",
        gap: "20px",
      }}
    >
      <CollapseCard title="Managed Network Devices">
        <Box sx={{ display: "flex", flexDirection: "column", width: 560 }}>
          <Box width="100%">
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{
                px: 1.5,
                pb: 1,
                borderBottom: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
                color: theme.palette.primary.main,
                marginBottom: 2,
              }}
            >
              <TitleTypography>Device Name</TitleTypography>
              <TitleTypography>MAC Address</TitleTypography>
              <TitleTypography>Model Name</TitleTypography>
              <TitleTypography>Status</TitleTypography>
            </Stack>

            <List disablePadding>
              {managedLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    margin: 2,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                managedData.map((item) => (
                  <ListItem
                    key={item.deviceId}
                    divider
                    disablePadding
                    sx={{
                      borderBottom: "none",
                    }}
                  >
                    <ListItemButton
                      onClick={() =>
                        pushPage(null, "devices", {
                          fetchFlag: true,
                          auth,
                          deviceId: item.deviceId,
                        })
                      }
                      sx={{
                        py: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 1.5,
                        cursor: "pointer",
                      }}
                    >
                      <ContentTypography>
                        {item.deviceName || "-"}
                      </ContentTypography>

                      <ContentTypography>
                        {item.deviceId || "-"}
                      </ContentTypography>

                      <ContentTypography>
                        {item.deviceModel || "-"}
                      </ContentTypography>

                      <ContentTypography>
                        <StatusTag
                          width="60px"
                          status={item.linkStatus}
                          statusMapping={DEVICE_STATUS_TAG}
                          labelMapping={STATUS_TAG_LABEL}
                        />
                      </ContentTypography>
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </Box>
        </Box>
        <Stack
          sx={{
            px: 1.5,
            pt: 2,
            borderTop: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
            marginTop: 2,
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: theme.typography.customFontSize[2],
              color: theme.palette.primary.black,
              textAlign: "left",
            }}
          >
            Showing {startRecord}–{endRecord} of {totalCount} records
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <PaginationField
              name="managedTable"
              pageCount={
                totalCount > 0
                  ? Math.max(1, Math.ceil(totalCount / rowsPerPage))
                  : 0
              }
              pageIndex={currentPage}
              pageSize={rowsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
              onPageSizeChange={(size) => setRowsPerPage(size)}
            />
          </Box>
        </Stack>
      </CollapseCard>
    </Box>
  );
};

export default ManagedContent;
